<template>
  <div>
    <v-layout row wrap>
      <v-flex shrink>
        <h2 class="headline pb-3">Payments</h2>
      </v-flex>
      <v-flex>
        <v-checkbox class="filter" v-model="currentOnly" label="current Only" color="primary"></v-checkbox>
      </v-flex>
      <v-flex shrink>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              flat
              icon
              color="primary"
              @click.stop="processSelectedPayments()"
              v-on="on"
            >
              <v-icon class="mr-1" color="success">done_outline</v-icon>
            </v-btn>
          </template>
          <span>Mark payments as processed</span>
        </v-tooltip>
      </v-flex>
    </v-layout>
    <v-divider></v-divider>
    <v-data-table
      v-model="selectedPayments"
      :headers="headers"
      :items="payments"
      select-all
      item-key="id"
      :rows-per-page-items="rowsPerPage"
    >
      <template v-slot:headers="props">
        <tr>
          <th>
            <v-checkbox
              :input-value="props.all"
              :indeterminate="props.indeterminate"
              primary
              hide-details
              @click.stop="toggleAll"
            ></v-checkbox>
          </th>
          <th
            v-for="header in props.headers"
            :key="header.text"
            class="text-xs-left"
          >
            <span class="font-weight-bold">{{ header.text }}</span>
          </th>
        </tr>
      </template>
      <template v-slot:items="props">
        <tr @click="showPayment(props.item, $event)">
          <td>
            <v-checkbox
              :disabled="!props.item.id"
              :input-value="props.selected"
              primary
              hide-details
              @change="props.selected = !props.selected"
            ></v-checkbox>
          </td>
          <td>
            <v-tooltip left>
              <template v-slot:activator="{ on }">
                <v-icon :color="props.item.stateColor" v-on="on">{{props.item.stateIcon}}</v-icon>
              </template>
              <span>{{props.item.stateTooltip}}</span>
            </v-tooltip>
          </td>
          <td>{{ props.item.paydue_time | moment('D/MM/YYYY') }}</td>
          <td :style="{color: props.item.paycolor}" v-if="props.item.pay_time">{{ props.item.pay_time | moment('D/MM/YYYY') }}</td>
          <td v-else></td>
          <td>{{ props.item.paystart_time | moment('D/MM/YYYY') }}</td>
          <td>{{ props.item.payend_time | moment('D/MM/YYYY') }}</td>
          <td>{{ '$' + props.item.pay_amount }}</td>
          <td :style="{color: props.item.color}">{{props.item.display_amount}}</td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import moment from 'moment'
import { mapGetters, mapActions } from 'vuex'
import alertHelper from '@/helpers/alert'
import paymentHelper from '@/helpers/payment'

export default {
  name: 'LeasePayments',
  data() {
    return {
      selectedPayments: [],
      selectedPayment: {},
      showSummary: false,
      currentOnly: true,
      rowsPerPage: [20, 50, 100, {text:"$vuetify.dataIterator.rowsPerPageAll", value: 20}],
      headers: [
        { text: 'Status', align: 'left', sortable: false, value: 'status' },
        {
          text: 'Pay Due Date',
          align: 'left',
          sortable: false,
          value: 'paydue_time',
        },
        {
          text: 'Pay Date',
          align: 'left',
          sortable: false,
          value: 'pay_time',
        },
        {
          text: 'Pay From Date',
          align: 'left',
          sortable: false,
          value: 'paystart_time',
        },
        {
          text: 'Pay To Date',
          align: 'left',
          sortable: false,
          value: 'payend_time',
        },
        {
          text: 'Total Rent',
          align: 'left',
          sortable: false,
          value: 'pay_amount'
        },
        {
          text: 'Total Amount',
          align: 'left',
          sortable: false,
          value: 'total_amount'
        },
      ],
    }
  },
  computed: {
    payments() {
      let payments = this.getPayments
      if (payments && payments.length > 0) {
        if (this.currentOnly) {
          const eightWeeksAgo = moment().subtract(8, 'w');
          payments = payments.filter(p => moment(p.paydue_time) >= eightWeeksAgo);
        }
        payments.map(payment => {
          payment.paycolor = this.delayed(payment)?'red': 'black'
          if (+payment.state === 0) {
            payment.display_amount = ''
            payment.stateIcon = 'error'
            payment.stateColor = 'grey'
            payment.stateTooltip = 'Unprocessed'
          } else {
            payment.display_amount = `$${payment.total_amount.toFixed(2)}`
            payment.color= (payment.total_amount >= 0)? 'black': 'red'
            if (payment.payment_pdf_send > 1) {
              payment.stateIcon = 'done_outline'
              payment.stateColor = 'warning'
              payment.stateTooltip = 'Failed to send statement'
            } else {
              payment.stateIcon = 'done_outline'
              payment.stateColor = 'success'
              payment.stateTooltip = 'Processed'
            }
          }
          return payment
        })
      }

      return payments
    },
    ...mapGetters({
      managerGstStatus: 'Configuration/getGstStatus',
      getPayments: 'HouseList/Lease/getPayments'
    })
  },
  methods: {
    ...mapActions({
      updateMany: 'HouseList/Lease/bulkUpdatePayments',
      processPayments: 'HouseList/Payments/processPayments',
      getLastBalance: 'HouseList/Payments/getLastBalance',
    }),
    delayed(payment) {
      const payduemoment = moment(payment.paydue_time);
      const paymoment = moment(payment.pay_time);
      return paymoment.diff(payduemoment, 'week') !== 0
    },
    toggleAll() {
      if (this.selectedPayments.length) {
        this.selectedPayments = []
      } else {
        this.selectedPayments = this.payments.slice()
      }
    },
    showPayment(payment, event) {
      if (payment.id && event.target.tagName.toLowerCase() === 'td') {
        this.$router.push({
          name: 'houseDetailLeasesPaymentsDetail',
          params: { paymentId: payment.id }
        })
      }
    },
    async processSelectedPayments() {
      if (!this.selectedPayments || !this.selectedPayments.length) {
        alertHelper.error(
          'Please select payments before processing.'
        )
      } else if (this.hasUnprocessedPayments()) {
        alertHelper.error(
          'Sorry, we cannot process your request. Please do not leave any gap in between your selection.'
        )
      } else {
        this.processPayments(this.selectedPayments)
        this.selectedPayments = []
      }
    },
    // Checks if there are any unprocessed payments between selected payments
    hasUnprocessedPayments() {
      // Create a copy of transactions and selectedPayments
      const transactions = JSON.parse(JSON.stringify(this.payments))
      const selectedPayments = JSON.parse(JSON.stringify(this.selectedPayments))

      // Only keep the unprocessed transactions
      const filteredTransactions = transactions.filter((t) => +t.state === 0)
      // Keep a map of only ids to make lookups easy
      const transactionsIdMap = filteredTransactions.map((transaction) => {
        return transaction.id
      })
      // Keep a map of selected payments ids for lookups
      let selectedPaymentsIdMap = []

      selectedPayments.forEach((selectedPayment) => {
        if (+selectedPayment.state === 0) {
          selectedPaymentsIdMap.push(selectedPayment.id)
        }
      })

      // Loop through the selectedPayments
      // Return true only
      // if the payment index is not 0
      // and the transaction before the current one is unprocessed
      // and the transaction before the current one does not exist in
      // selected payments
      for (let selectedPayment of this.selectedPayments) {
        const index = transactionsIdMap.indexOf(selectedPayment.id)

        if (
          index !== -1 &&
          index !== 0 &&
          +filteredTransactions[index - 1].state === 0 &&
          selectedPaymentsIdMap.indexOf(filteredTransactions[index - 1].id) ===
            -1
        ) {
          return true
        }
      }
      return false
    },
  }
}
</script>

<style>
.filter {
  margin: 4px;
}
</style>
